import { useRef, useState } from "react";
import { Upload, X, Star, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

// photos: array of storage paths, mainIndex: number
export const PhotoUploader = ({ photos, mainIndex, onChange }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const mainPath = photos[mainIndex];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 12) {
      toast.error("Maximum 12 photos per vehicle");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...photos, ...data.paths], mainIndex);
      toast.success(`${data.paths.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Session expired — please log in again" : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const removedPath = photos[idx];
    const next = photos.filter((_, i) => i !== idx);
    let nextMain = next.indexOf(mainPath);
    if (removedPath === mainPath || nextMain === -1) nextMain = 0;
    onChange(next, Math.max(0, Math.min(nextMain, next.length - 1)));
  };

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next, Math.max(0, next.indexOf(mainPath)));
  };

  const onDrop = (idx) => {
    reorder(dragIndex, idx);
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div data-testid="photo-uploader">
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} data-testid="photo-file-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || photos.length >= 12}
        className="w-full border-2 border-dashed border-slate-600 rounded-xl py-6 flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
        data-testid="photo-upload-trigger"
      >
        {uploading ? <Loader2 className="animate-spin" size={22} /> : <Upload size={22} />}
        <span className="text-sm font-medium">
          {uploading ? "Uploading…" : `Upload photos (${photos.length}/12)`}
        </span>
      </button>

      {photos.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
            <GripVertical size={12} /> Drag photos to reorder · click the star to set the main photo
          </p>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div
                key={p}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                onDrop={() => onDrop(i)}
                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                className={`relative group aspect-square rounded-lg overflow-hidden border cursor-move transition-all ${
                  overIndex === i && dragIndex !== null ? "border-red-500 ring-2 ring-red-500/50" : "border-slate-700"
                } ${dragIndex === i ? "opacity-40" : ""}`}
                data-testid={`photo-item-${i}`}
              >
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover pointer-events-none" />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-rose-600"
                  data-testid={`photo-remove-${i}`}
                >
                  <X size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(photos, i)}
                  className={`absolute bottom-1 left-1 rounded-full p-1 ${
                    mainIndex === i ? "bg-red-600 text-white" : "bg-black/60 text-white hover:bg-red-600"
                  }`}
                  title="Set as main photo"
                  data-testid={`photo-set-main-${i}`}
                >
                  <Star size={12} fill={mainIndex === i ? "currentColor" : "none"} />
                </button>
                {mainIndex === i && (
                  <span className="absolute top-1 left-1 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">MAIN</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
